import { LockOutlinedIcon, Ride, RideService } from '@geovelo-frontends/commons';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  SvgIconProps,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../components/button';
import Dialog from '../../components/dialog';
import { PublicIcon } from '../../components/icons/public';

export type TPrivacy = 'private' | 'public';

export const privacies: TPrivacy[] = ['private', 'public'];

export const privaciesMap: {
  [key in TPrivacy]: {
    descriptionKey: string;
    Icon: (props: SvgIconProps) => JSX.Element;
    labelKey: string;
    titleKey: string;
  };
} = {
  private: {
    Icon: LockOutlinedIcon,
    titleKey: 'geovelo.ride_creation.visibilities.private_title',
    descriptionKey: 'geovelo.ride_creation.visibilities.private_description',
    labelKey: 'geovelo.rides.detail.visibilities.private',
  },
  public: {
    Icon: PublicIcon,
    titleKey: 'geovelo.ride_creation.visibilities.public_title',
    descriptionKey: 'geovelo.ride_creation.visibilities.public_description',
    labelKey: 'geovelo.rides.detail.visibilities.public',
  },
};

function PrivacyDialog({
  ride,
  onClose,
  ...props
}: Omit<DialogProps, 'onClose'> & {
  onClose: (updatedRide?: Ride) => void;
  ride: Ride | undefined;
}): JSX.Element {
  const [privacy, setPrivacy] = useState<TPrivacy>('private');
  const [isSubmitting, setSubmitting] = useState(false);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (props.open && ride?.privacy && ride.privacy !== 'forcePrivate') setPrivacy(ride.privacy);
  }, [props.open, ride]);

  async function handleSubmit() {
    if (!ride?.id || ride.privacy === 'forcePrivate') return;

    setSubmitting(true);

    try {
      const updatedRide = await RideService.updateRide(ride.id, { privacy });

      enqueueSnackbar(t('geovelo.rides.detail.privacy_dialog.success'));
      onClose(updatedRide);
    } catch (err) {
      enqueueSnackbar(t('geovelo.rides.detail.privacy_dialog.server_error'), { variant: 'error' });
    }

    setSubmitting(false);
  }

  return (
    <Dialog
      disableEscapeKeyDown
      fullWidth
      maxWidth="sm"
      onClose={(_, reason) => (!isSubmitting || reason !== 'backdropClick') && onClose()}
      {...props}
    >
      <DialogTitle>{t('geovelo.rides.detail.privacy_dialog.title')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <RadioGroup
          onChange={({ target: { value } }) => setPrivacy(value as TPrivacy)}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          value={privacy}
        >
          {privacies.map((key) => {
            const { Icon, titleKey, descriptionKey } = privaciesMap[key];
            const active = key === privacy;

            return (
              <FormControlLabel
                disableTypography
                control={<Radio />}
                key={key}
                label={
                  <Box display="flex" flexDirection="column">
                    <Box alignItems="center" display="flex" gap={1}>
                      <Icon color="primary" fontSize="small" />
                      <Typography fontWeight={600}>{t(titleKey)}</Typography>
                    </Box>
                    <Typography color="textSecondary" variant="body2">
                      {t(descriptionKey)}
                    </Typography>
                  </Box>
                }
                sx={({ palette }) => ({
                  backgroundColor: active ? '#f3f7fd' : 'transparent',
                  border: `1px solid ${active ? palette.primary.main : '#c0c7cc'}`,
                  borderRadius: 2,
                  gap: 2,
                  marginX: 0,
                  padding: 2,
                })}
                value={key}
              />
            );
          })}
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} size="small">
          {t('commons.actions.cancel')}
        </Button>
        <Button color="primary" onClick={handleSubmit} size="small" variant="contained">
          {t('commons.actions.validate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PrivacyDialog;
